import { Box, Button, Grid, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery, useTheme } from '@mui/material';
import TransactionTypes from './components/TransactionTypes';
import { Plus, Trash01 } from '../../assets';
import CreateReasonModal from './modals/CreateReason';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../components/shared/useUser';
import { PostReasonRequest, Reason } from '../../app/services/api/generated';
import useNswagClient from '../../hooks/api/useNswagClient';

import EditReasonModal from './modals/EditReason';
import DeleteReasonModal from './modals/DeleteReason';
import useLogError from '../../hooks/useLogError';
import LoadingWheel from '../../components/ui/LoadingWheel';
import { useTranslation } from 'react-i18next';
import MessagesSnackbar from './modals/CreateMessageConfirm';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';

const TransactionsManagement = () => {
  const { selectedStore, hasPermissionTo, isAdmin } = useContext(UserContext);
  const theme = useTheme();
  const [reasons, setReasons] = useState<Reason[]>([]);
  const { getTransactionReasons, putTransactionReasons } = useNswagClient();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [hasSaveReason, setHasSaveReason] = useState(false);
  const [createMessage, setCreateMessage] = useState(false);
  const [editMessage, setEditMessage] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState(false);
  const [maxWasteLogError, setMaxWasteLogError] = useState(false);
  const [reasonId, setReasonId] = useState<number>(0);
  const [description, setDescription] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [actionType, setActionType] = useState<string>('');
  const [includeInWasteLog, setIncludeInWasteLog] = useState<boolean>(false);
  const [wasteLogSortOrder, setWasteLogSortOrder] = useState<number | null>(null);
  const { logError } = useLogError();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation('manageTransactions');
  const nameOfReason = 'Mark Out';

  const wasteLogReasonsCount = reasons.filter(r => r.includeInWasteLog).length;

  // Permission checks for Transaction Reasons
  const canViewReasons = hasPermissionTo(['TransactionReasonsRead']) || isAdmin();
  const canWriteReasons = hasPermissionTo(['TransactionReasonsWrite']) || isAdmin();

  useEffect(() => {
    setLoading(true);
    getReasons();
  }, [hasSaveReason]);

  const handleClose = () => {
    setOpen(false);
    setOpenEdit(false);
    setOpenDelete(false);
  };

  const handleCreateSuccess = () => {
    setHasSaveReason(!hasSaveReason);
    setOpen(false);
    setCreateMessage(true);
  };

  const handleEditSuccess = () => {
    setHasSaveReason(!hasSaveReason);
    setOpenEdit(false);
    setEditMessage(true);
  };

  const handleDeleteSuccess = () => {
    setHasSaveReason(!hasSaveReason);
    setOpenDelete(false);
    setDeleteMessage(true);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const getReasons = () => {
    const storeNumber = selectedStore?.storeNumber;
    if (storeNumber) {
      setError(null);

      // Check permissions before making API call
      if (!canViewReasons) {
        setLoading(false);
        setError('Insufficient permissions to view transaction reasons');
        return;
      }

      getTransactionReasons('', storeNumber).then((result) => {
        if (result) {
          setLoading(false);
          const reasons = result.data?.reasons;
          if (reasons) {
            // Ensure includeInWasteLog defaults to false if not present
            const reasonsWithDefaults = reasons.map(r => ({
              ...r,
              includeInWasteLog: r.includeInWasteLog ?? false,
            }));
            // Sort by wasteLogSortOrder (if exists) first, then by name
            const sortedReasons = reasonsWithDefaults.sort((a, b) => {
              // Items with sort order come first
              const aHasOrder = a.wasteLogSortOrder != null;
              const bHasOrder = b.wasteLogSortOrder != null;

              if (aHasOrder && !bHasOrder) return -1;
              if (!aHasOrder && bHasOrder) return 1;
              if (aHasOrder && bHasOrder) {
                return (a.wasteLogSortOrder ?? 0) - (b.wasteLogSortOrder ?? 0);
              }
              // Both don't have sort order - sort by name
              return (a.name ?? '').localeCompare(b.name ?? '');
            });
            setReasons(sortedReasons);
            setLoading(false);
          }
        } else {
          logError(result);
        }
      })
        .catch((error) => {
          setLoading(false);
          const status = error?.response?.status || error?.status;
          if (status === 403) {
            setError('Insufficient permissions to view transaction reasons');
          } else {
            setError('Failed to load transaction reasons');
          }
          logError(error);
        });
    }
  };

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>, rowId: number, name: string, description: string, actionType: string, includeInWasteLog: boolean, wasteLogSortOrder: number | null) => {
    if ((event.target as HTMLTableRowElement).classList.contains('delete-icon')) {
      return;
    }
    setReasonId(rowId);
    setName(name);
    setDescription(description);
    setActionType(actionType);
    setIncludeInWasteLog(includeInWasteLog);
    setWasteLogSortOrder(wasteLogSortOrder);
    setOpenEdit(true);
  };

  const handleOnDelete = (rowId: number, name: string) => {
    setReasonId(rowId);
    setName(name);
    setOpenDelete(true);
  };

  const handleToggleWasteLog = (event: React.ChangeEvent<HTMLInputElement>, reason: Reason) => {
    event.stopPropagation();
    const newValue = event.target.checked;

    // Check if trying to enable and already at max
    if (newValue && !reason.includeInWasteLog && wasteLogReasonsCount >= 4) {
      setMaxWasteLogError(true);
      return;
    }

    // Optimistically update local state first
    setReasons(prevReasons =>
      prevReasons.map(r =>
        r.id === reason.id
          ? { ...r, includeInWasteLog: newValue }
          : r,
      ),
    );

    // Update via API with all required fields
    const body: PostReasonRequest = {
      name: reason.name ?? '',
      description: reason.description ?? '',
      actionType: reason.actionType ?? '',
      includeInWasteLog: newValue,
      wasteLogSortOrder: newValue ? reason.wasteLogSortOrder ?? undefined : undefined,
    };

    putTransactionReasons(reason.id ?? 0, body)
      .then(() => {
        // Success - state already updated
      })
      .catch((error) => {
        logError(error);
        // Revert on error
        setReasons(prevReasons =>
          prevReasons.map(r =>
            r.id === reason.id
              ? { ...r, includeInWasteLog: !newValue }
              : r,
          ),
        );
      });
  };

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12} >
          <PageTitle>{t('typesTitle')}</PageTitle>
        </Grid>
        <Grid item
          xs={12}
          sx={{ pt: '10px' }}>
          <TransactionTypes />
        </Grid>
      </Grid>
      <Grid container
        spacing={5}
        sx={{ pt: '20px' }}>
        <Grid item
          xs={isMobile ? 8 : 7} >
          <PageTitle>
            {t('reasonsTitle')}
          </PageTitle>
        </Grid>
        <Grid item
          xs={isMobile ? 4 : 5}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {canWriteReasons && (
            <Button
              size='lg'
              sx={{ mr: 3 }}
              variant='primary'
              onClick={handleOpen}
            ><Plus />{t('add')}
            </Button>
          )}
        </Grid>
        <Grid item
          xs={12}
          sx={{ pt: '10px' }}>
          <Box sx={{
            border: '1px solid',
            borderColor: theme.palette.custom.gray[200],
            marginTop: '10px',
          }}>
            {loading ? <LoadingWheel /> : error ? (
              <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
                {error}
              </Box>
            ) : (
              <TableContainer sx={{ overflowy: 'auto' }}
              >
                <Table
                  size="small"
                  aria-label="a table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('name')}</TableCell>
                      <TableCell>{t('description')}</TableCell>
                      <TableCell>{t('includeInWasteLog')}</TableCell>
                      <TableCell>{t('sortOrder')}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reasons?.map((row: Reason) => (
                      <TableRow
                        hover
                        key={row.id}
                        onClick={canWriteReasons ? (event) => handleRowClick(event, row?.id ?? 0, row?.name ?? '', row.description ?? '', row.actionType ?? '', row.includeInWasteLog ?? false, row.wasteLogSortOrder ?? null) : undefined}
                        sx={{ cursor: canWriteReasons ? 'pointer' : 'default' }}
                      >
                        <TableCell >
                          {row.name}
                        </TableCell>
                        <TableCell>
                          {row.description}
                        </TableCell>
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          <Switch
                            checked={row.includeInWasteLog ?? false}
                            onChange={(event) => handleToggleWasteLog(event, row)}
                            disabled={!canWriteReasons}
                          />
                        </TableCell>
                        <TableCell>
                          {row.includeInWasteLog ? (row.wasteLogSortOrder ?? '-') : ''}
                        </TableCell>
                        <TableCell onClick={(event) => event.stopPropagation()}>
                          {row.name !== nameOfReason && canWriteReasons && (
                            <Trash01
                              color={theme.palette.error[500]}
                              className='delete-icon'
                              onClick={() => handleOnDelete(row.id ?? 0, row.name ?? '')} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Grid>
      </Grid>
      {open ?
        <CreateReasonModal
          onClose={handleClose}
          onConfirm={handleCreateSuccess}
          wasteLogReasonsCount={wasteLogReasonsCount}
          existingReasons={reasons}
        /> : null}
      {openEdit ?
        <EditReasonModal
          onClose={handleClose}
          onConfirm={handleEditSuccess}
          reasonId={reasonId}
          name={name}
          description={description}
          actionType={actionType}
          includeInWasteLog={includeInWasteLog}
          wasteLogSortOrder={wasteLogSortOrder}
          wasteLogReasonsCount={wasteLogReasonsCount}
          existingReasons={reasons}
        /> : null}
      {openDelete ?
        <DeleteReasonModal
          onClose={handleClose}
          onConfirm={handleDeleteSuccess}
          reasonId={reasonId}
          name={name}
        /> : null}
      <MessagesSnackbar
        open={createMessage}
        onClose={() => setCreateMessage(false)}
        message={t('createMessage')}
        severity="success"
      />
      <MessagesSnackbar
        open={editMessage}
        onClose={() => setEditMessage(false)}
        message={t('editMessage')}
        severity="success"
      />
      <MessagesSnackbar
        open={deleteMessage}
        onClose={() => setDeleteMessage(false)}
        message={t('deleteMessage')}
        severity="success"
      />
      <MessagesSnackbar
        open={maxWasteLogError}
        onClose={() => setMaxWasteLogError(false)}
        message={t('maxWasteReasonsError')}
        severity="error"
      />
    </PageArea>
  );
};

export default TransactionsManagement;